import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useSettings } from "../settings";

const HERO_SRC = "/assets/hero.png";
const HERO_FALLBACK = "https://images.pexels.com/photos/31197870/pexels-photo-31197870.jpeg";

const STATS = [
  { value: "Design → Supply → Erection", label: "True turnkey, one call" },
  { value: "Residential & Commercial", label: "Backyard shops to big iron" },
  { value: "One Crew, Start to Finish", label: "No handoffs, no runaround" },
];

export const Hero = () => {
  const ref = useRef(null);
  const [imgSrc, setImgSrc] = useState(HERO_SRC);
  const { settings } = useSettings();
  const lines = [settings.hero_line_1, settings.hero_line_2, settings.hero_line_3].filter(Boolean);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const go = (e, href) => {
    e.preventDefault();
    window.__lenis?.scrollTo(href, { offset: -70 });
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={imgSrc}
          onError={() => imgSrc !== HERO_FALLBACK && setImgSrc(HERO_FALLBACK)}
          alt="Steel building under erection at dusk"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/10 to-black/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />

      <div className="relative z-10 flex flex-1 flex-col justify-center px-5 pt-32 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6"
        >
          <Logo className="h-24 w-24 sm:h-28 sm:w-28" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 text-[11px] font-medium uppercase tracking-[0.4em] text-turquoise sm:text-xs"
          data-testid="hero-eyebrow"
        >
          {settings.hero_eyebrow}
        </motion.p>

        <h1 className="font-heading text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="hero-heading">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === lines.length - 1 ? "text-volt" : "text-white"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base"
          data-testid="hero-subtext"
        >
          {settings.hero_subtext}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#quote"
            onClick={(e) => go(e, "#quote")}
            data-testid="hero-quote-button"
            className="group flex items-center gap-2 bg-volt px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
          >
            Price My Building
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#gallery"
            onClick={(e) => go(e, "#gallery")}
            data-testid="hero-gallery-button"
            className="border border-white/30 px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-turquoise hover:text-turquoise"
          >
            See Our Work
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="relative z-10 border-t border-white/10 bg-black/40 backdrop-blur-sm"
      >
        <div className="grid grid-cols-1 divide-y divide-white/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {STATS.map((s) => (
            <div key={s.value} className="px-6 py-5 lg:px-12">
              <p className="font-heading text-sm font-bold uppercase tracking-wider text-white">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-white/50">{s.label}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-28 right-8 z-10 hidden lg:block"
      >
        <ArrowDown className="h-5 w-5 text-white/40" />
      </motion.div>
    </section>
  );
};
